import * as Sentry from '@sentry/browser';
import HttpMethod from './HttpMethod';
import HttpStatusCode from './HttpStatusCode';
import TimeZone from './Timezone';
import type { Configuration, IRequest } from './Types';
import axios, {
  type AxiosError,
  type AxiosInstance,
  type CancelTokenSource,
} from 'axios';

import { getAuthHeaders } from '../account/session/AuthToken';
import { getOpenTelemetryBaggageHttpHeaders } from '../openTelemetryBaggage';

export class CobliAxios {
  private axiosInstance: AxiosInstance;

  private configBaseURL: string;

  constructor(configuration: Configuration) {
    this.axiosInstance = axios.create();
    this.configBaseURL = configuration.host;
  }

  public request = (
    requestData: IRequest,
    cancelToken?: CancelTokenSource,
    nonReportableToSentryErrors: HttpStatusCode[] = [],
    external = false
  ) => {
    const cobliHeaders = {
      ...getAuthHeaders(),
      'cobli-timezone': TimeZone.getTimezone(),
      ...getOpenTelemetryBaggageHttpHeaders(),
    };

    const headers: Record<string, string> = !external
      ? {
          ...cobliHeaders,
          'Content-type': 'application/json',
          ...requestData.headers,
        }
      : { 'Content-type': 'application/json', ...requestData.headers };

    return this.axiosInstance
      .request({
        url: requestData.endpoint,
        baseURL: requestData.externalBaseURL || this.configBaseURL,
        method: HttpMethod[requestData.method],
        headers,
        data: requestData.body,
        params: requestData.params,
        /*
      The default responseType is json, so it's not need to pass.
      See more in https://github.com/axios/axios#request-config
      */
        responseType: requestData.responseType,
        /*
      Generating a cancel token to make cancelation available to rest of code
      */
        cancelToken: cancelToken?.token,

        /*
      We need withCredential to make browser accept and send security headers.
      i.e. Cookie/Set-Cookie
      */
        withCredentials: requestData.withCredentials || false,
      })
      .catch((error) => {
        if (
          error.response &&
          !nonReportableToSentryErrors.includes(error.response.status)
        ) {
          this.sentryCatch(requestData, error);
        }

        throw error;
      });
  };

  private sentryCatch = (requestData: IRequest, error: AxiosError) => {
    Sentry.withScope((scope) => {
      scope.setExtra('url', error.config.url);
      scope.setExtra('type', error.config.method);
      scope.setExtra('error', error.message);

      if (error.response) {
        scope.setExtra('status', error.response.status);
      }

      if (
        requestData.responseType !== 'arraybuffer' &&
        error.request &&
        error.request.responseText
      ) {
        scope.setExtra(
          'response',
          error.request.responseText.substring(0, 1024)
        );
      }

      Sentry.captureException(error);

      if (error.response) {
        switch (error.response.status) {
          case HttpStatusCode.UNAUTHORIZED:
            // TODO: use fancy history object instead to help mocking and stuff
            window.location.reload();
        }
      }
    });
  };
}
